import { Trans } from "@lingui/macro";
import { Button } from "@reactive-resume/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import {
  isAdsTrackingConfigured,
  isAutomatedBrowser,
} from "@/client/services/analytics/google-analytics";

const CONSENT_STORAGE_KEY = "apply-edge-consent-v2";
const CONSENT_EXPIRY_DAYS = 365;

export type ConsentChoice = "accepted" | "rejected" | "not_set";

type CookieConsentState = {
  choice: ConsentChoice;
  timestamp: number;
};

/**
 * Get current consent state from localStorage
 */
function getConsentState(): CookieConsentState | null {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const state = JSON.parse(stored) as CookieConsentState;
    const expiryTime = state.timestamp + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    // Consent expired, treat as not set
    if (Date.now() > expiryTime) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    return state;
  } catch {
    return null;
  }
}

/**
 * Save consent choice to localStorage and update GA Consent Mode
 */
function saveConsentChoice(choice: ConsentChoice) {
  const state: CookieConsentState = {
    choice,
    timestamp: Date.now(),
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));

  // Update Google Consent Mode v2
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof window !== "undefined" && window.gtag && !isAutomatedBrowser()) {
    // Ad signals track the same accept/reject choice, but only where ads are
    // actually configured. Hard-denying them here (as this did while we ran no
    // ads) would silently override the grant set at init and make enhanced
    // conversions a no-op.
    const adConsent =
      choice === "accepted" && isAdsTrackingConfigured()
        ? ("granted" as const)
        : ("denied" as const);

    window.gtag("consent", "update", {
      analytics_storage: choice === "accepted" ? "granted" : "denied",
      ad_storage: adConsent,
      ad_user_data: adConsent,
    });
  }
}

/**
 * Restore previously-accepted consent on page init
 * Called during analytics initialization to re-grant analytics_storage
 * if the user previously accepted and consent hasn't expired
 */
export function restoreConsentIfAccepted() {
  const state = getConsentState();
  // Implied-consent model: analytics defaults to granted at init. The only
  // restore needed is DOWNGRADING for users who explicitly rejected before
  // (their stored choice keeps being honored).
  if (state?.choice === "rejected" && !isAutomatedBrowser() && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      // An explicit rejection outranks the Ads-ID grant from init.
      ad_user_data: "denied",
    });
  }
}

/**
 * Analytics notice. Inline placement keeps task pages free of overlays.
 */
export const CookieConsent = ({ inline = false }: { inline?: boolean }) => {
  const [showBanner, setShowBanner] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const suppressed = ["/auth", "/onboarding", "/builder"].some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );
  const afterDashboardContent =
    location.pathname === "/applications" ||
    location.pathname === "/applications/tracker" ||
    location.pathname === "/dashboard/resumes";
  const visible =
    showBanner && !suppressed && (inline || !/^\/auto-apply\/?$/.test(location.pathname));

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getConsentState();
    setShowBanner(!consent);
  }, [location.pathname]);

  useEffect(() => {
    if (!visible || inline || afterDashboardContent || !bannerRef.current) return;
    const originalPadding = document.body.style.paddingBottom;
    const banner = bannerRef.current;
    const updatePadding = () => {
      document.body.style.paddingBottom =
        window.innerWidth < 640
          ? `${banner.getBoundingClientRect().height + 8}px`
          : originalPadding;
    };
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePadding);
    observer?.observe(banner);
    window.addEventListener("resize", updatePadding);
    updatePadding();
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updatePadding);
      document.body.style.paddingBottom = originalPadding;
    };
  }, [visible, inline, afterDashboardContent]);

  const handleAccept = useCallback(() => {
    saveConsentChoice("accepted");
    setShowBanner(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      data-ssg-strip="consent-banner"
      className={
        inline
          ? "border-t border-border py-4"
          : (afterDashboardContent
            ? "mx-2 mb-2 max-w-xl rounded-2xl border border-border bg-surface/95 p-2 shadow-2xl backdrop-blur-md sm:mx-auto sm:mb-4 sm:p-4"
            : "fixed inset-x-2 bottom-2 z-50 mx-auto max-w-xl rounded-2xl border border-border bg-surface/95 p-2 shadow-2xl backdrop-blur-md sm:inset-x-4 sm:bottom-4 sm:p-4")
      }
    >
      <div className="flex flex-row items-center gap-2 sm:gap-3">
        <p className="flex-1 text-xs text-foreground/80 sm:text-sm">
          <Trans>
            By continuing to use Apply Edge you accept our use of analytics cookies to improve the
            product.{" "}
            <Link to="/privacy" className="underline hover:text-foreground">
              Learn more
            </Link>
          </Trans>
        </p>
        <Button size="sm" className="min-h-11 min-w-11 shrink-0" onClick={handleAccept}>
          <Trans>OK</Trans>
        </Button>
      </div>
    </div>
  );
};
